import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import PrintHeader from '../Common/PrintHeader';
import './Forms.css';

const AnnualReconciliationForm = () => {
  const { t } = useTranslation();
  const printRef = useRef(null);
  const [formData, setFormData] = useState({
    logo: null,
    companyNameAr: '',
    companyNameEng: '',
    activitiesAr: '',
    activitiesEng: '',
    commercialRegistration: '',
    issuedIn: '',
    issuedInEng: '',
    firstPartyName: '',
    firstPartyId: '',
    firstPartyNationality: '',
    secondPartyType: 'individual', // 'individual' or 'company'
    secondParty: '',
    secondPartyEng: '',
    secondPartyCompanyName: '',
    secondPartyCompanyNameEng: '',
    secondPartyCommercialRegistration: '',
    secondPartyAgentName: '',
    secondPartyAgentId: '',
    secondPartyPowerOfAttorneyNumber: '',
    propertyType: '',
    propertyDistrict: '',
    propertyPlotNumber: '',
    periodFrom: '',
    periodTo: '',
    clause1: '',
    clause2: '',
    clause3: '',
    clause4: '',
    clause5: '',
    firstPartySignature: '',
    secondPartySignature: '',
    addressAr: '',
    addressEng: '',
    phone1: '',
    phone2: '',
    phone3: '',
    email: '',
    date: '',
  });

  const fillSampleData = () => {
    const today = new Date();
    const yearStart = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0];
    const yearEnd = new Date(today.getFullYear(), 11, 31).toISOString().split('T')[0];
    
    setFormData({
      logo: null,
      companyNameAr: 'السالمي للخدمات العقارية',
      companyNameEng: 'AL-Salmi Contracting and real estate services',
      activitiesAr: 'بيع - شراء - إدارة الأملاك - استثمار - توثيق عقود الإيجار (إلكتروني)',
      activitiesEng: 'Sale - Purchase - Property Management - Investment - Documenting Rental Contracts (Electronic)',
      commercialRegistration: '2050046029',
      issuedIn: 'الدمام',
      issuedInEng: 'Dammam',
      firstPartyName: 'محمد أحمد السالمي',
      firstPartyId: '1001234567',
      firstPartyNationality: 'سعودي',
      secondPartyType: 'individual',
      secondParty: 'مكتب السالمي للخدمات العقارية',
      secondPartyEng: 'Al-Salmi Real Estate Services Office',
      secondPartyCompanyName: '',
      secondPartyCompanyNameEng: '',
      secondPartyCommercialRegistration: '',
      secondPartyAgentName: '',
      secondPartyAgentId: '',
      secondPartyPowerOfAttorneyNumber: '',
      propertyType: 'شقة سكنية',
      propertyDistrict: 'حي بدر',
      propertyPlotNumber: '1234',
      periodFrom: yearStart,
      periodTo: yearEnd,
      clause1: 'تم استلام جميع المستحقات المطلوبة من المستأجرين من الطرف الثاني أو ممثله ونقلها إلى حساب الطرف الأول، مع تقديم نسخة من دفتر العقار لنفس الفترة.',
      clause2: 'لا توجد لدى الطرف الأول ملاحظات نهائية أو اعتراضات بخصوص الدفتر المالي للعقار المذكور.',
      clause3: 'تم خصم المبلغ المستحق للمكتب لإدارة الأملاك، ولا يطالب الطرف الثاني (أو الطرف الأول) بأي مبلغ إضافي خلال الفترة المحددة.',
      clause4: 'يتنازل الطرف الأول (أو ممثله) عن أي مطالبات مالية أو غير ذلك ضد الطرف الثاني (أو ممثله)، سواء في الوقت الحاضر أو في المستقبل، فيما يتعلق بالفترة المغطاة بهذه المخالصة.',
      clause5: 'تمت هذه المخالصة السنوية بين الطرفين بكامل الأهلية العقلية، دون إكراه، وأن كل طرف حصل على نسخة، وهي تعتبر وثيقة معتمدة وقابلة للتنفيذ من قبل الجهات المختصة.',
      firstPartySignature: 'محمد أحمد السالمي',
      secondPartySignature: 'مكتب السالمي للخدمات العقارية',
      addressAr: 'الدمام - المملكة العربية السعودية',
      addressEng: 'Dammam - Kingdom of Saudi Arabia',
      phone1: '0501234567',
      phone2: '0559876543',
      phone3: '',
      email: 'alsalemi.1aqar@gmail.com',
      date: today.toISOString().split('T')[0],
    });
  };

  const handleChange = async (e) => {
    const { name, value, type, files } = e.target;
    if (type === 'file') {
      const file = files[0];
      setFormData({ ...formData, [name]: file });
      
      // File is stored in formData, no need for separate logoDataUrl
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handlePrint = async () => {
    try {
      const element = printRef.current;
      
      // Wait for images to load
      const images = element.querySelectorAll('img');
      const imagePromises = Array.from(images).map((img) => {
        if (img.complete && img.naturalHeight !== 0) {
          return Promise.resolve();
        }
        return new Promise((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = () => resolve(); // Resolve even on error to continue
          // Timeout after 5 seconds
          setTimeout(() => resolve(), 5000);
        });
      });
      
      await Promise.all(imagePromises);

      // Small delay to ensure rendering is complete
      await new Promise(resolve => setTimeout(resolve, 100));

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
        backgroundColor: '#ffffff',
        imageTimeout: 15000,
        removeContainer: false,
      });
      
      const imgData = canvas.toDataURL('image/png', 1.0);
      const pdf = new jsPDF('portrait', 'mm', 'a4');
      const pageWidth = 210;
      const imgWidth = pageWidth * 0.7; // 30% smaller
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const marginX = (pageWidth - imgWidth) / 2;
      let heightLeft = imgHeight;

      let position = 0;
      pdf.addImage(imgData, 'PNG', marginX, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', marginX, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      const pdfBlob = pdf.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, '_blank');
    } catch (error) {
      console.error('Print error:', error);
      alert('Failed to generate PDF. Please try again. Error: ' + error.message);
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-title">
          <i className="fas fa-file-contract page-icon"></i>
          <h1>{t('forms.annualReconciliation.title', 'Annual Reconciliation for Property Management')}</h1>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={fillSampleData}
          style={{ backgroundColor: '#28a745' }}
        >
          <i className="fas fa-fill"></i> {t('forms.fillSampleData', 'Fill Sample Data')}
        </button>
      </div>

      <div className="form-container">
        <form>
          {/* Company Information */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-image"></i> {t('forms.logo', 'Logo')}
              </label>
              <div className="input-wrapper">
                <input
                  type="file"
                  className="form-input"
                  name="logo"
                  accept="image/*"
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-building"></i> {t('forms.companyNameAr', 'Company Name (Arabic)')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-building input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="companyNameAr"
                  value={formData.companyNameAr}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-building"></i> {t('forms.companyNameEng', 'Company Name (English)')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-building input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="companyNameEng"
                  value={formData.companyNameEng}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-file-contract"></i> {t('forms.commercialRegistration', 'Commercial Registration')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-file-contract input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="commercialRegistration"
                  value={formData.commercialRegistration}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-briefcase"></i> {t('forms.activitiesAr', 'Activities (Arabic)')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-briefcase input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="activitiesAr"
                  value={formData.activitiesAr}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-briefcase"></i> {t('forms.activitiesEng', 'Activities (English)')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-briefcase input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="activitiesEng"
                  value={formData.activitiesEng}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-map-marker-alt"></i> {t('forms.issuedIn', 'Issued In (Arabic)')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-map-marker-alt input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="issuedIn"
                  value={formData.issuedIn}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-map-marker-alt"></i> {t('forms.issuedInEng', 'Issued In (English)')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-map-marker-alt input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="issuedInEng"
                  value={formData.issuedInEng}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          {/* Parties Information */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-user"></i> {t('forms.firstPartyName', 'First Party Name')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-user input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="firstPartyName"
                  value={formData.firstPartyName}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-id-card"></i> {t('forms.firstPartyId', 'First Party ID')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-id-card input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="firstPartyId"
                  value={formData.firstPartyId}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-flag"></i> {t('forms.firstPartyNationality', 'First Party Nationality')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-flag input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="firstPartyNationality"
                  value={formData.firstPartyNationality}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-calendar"></i> {t('forms.date', 'Date')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-calendar input-icon"></i>
                <input
                  type="date"
                  className="form-input"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-user-tag"></i> {t('forms.secondPartyType', 'Second Party Type')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-user-tag input-icon"></i>
                <select
                  className="form-input"
                  name="secondPartyType"
                  value={formData.secondPartyType}
                  onChange={handleChange}
                >
                  <option value="individual">{t('forms.individual', 'Individual')} / فرد</option>
                  <option value="company">{t('forms.company', 'Company')} / شركة</option>
                </select>
              </div>
            </div>
          </div>

          {formData.secondPartyType === 'individual' ? (
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">
                  <i className="fas fa-user"></i> {t('forms.secondParty', 'Second Party (Arabic)')}
                </label>
                <div className="input-wrapper">
                  <i className="fas fa-user input-icon"></i>
                  <input
                    type="text"
                    className="form-input"
                    name="secondParty"
                    value={formData.secondParty}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">
                  <i className="fas fa-user"></i> {t('forms.secondPartyEng', 'Second Party (English)')}
                </label>
                <div className="input-wrapper">
                  <i className="fas fa-user input-icon"></i>
                  <input
                    type="text"
                    className="form-input"
                    name="secondPartyEng"
                    value={formData.secondPartyEng}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-building"></i> {t('forms.secondPartyCompanyName', 'Company Name (Arabic)')} / اسم الشركة
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-building input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="secondPartyCompanyName"
                      value={formData.secondPartyCompanyName}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-building"></i> {t('forms.secondPartyCompanyNameEng', 'Company Name (English)')}
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-building input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="secondPartyCompanyNameEng"
                      value={formData.secondPartyCompanyNameEng}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-file-contract"></i> {t('forms.secondPartyCommercialRegistration', 'Commercial Registration')} / السجل التجاري
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-file-contract input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="secondPartyCommercialRegistration"
                      value={formData.secondPartyCommercialRegistration}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-user-tie"></i> {t('forms.secondPartyAgentName', 'Agent Name')} / اسم الوكيل
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-user-tie input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="secondPartyAgentName"
                      value={formData.secondPartyAgentName}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-id-card"></i> {t('forms.secondPartyAgentId', 'Agent ID Number')} / رقم هوية الوكيل
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-id-card input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="secondPartyAgentId"
                      value={formData.secondPartyAgentId}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-file-signature"></i> {t('forms.secondPartyPowerOfAttorneyNumber', 'Power of Attorney Number')} / رقم الوكالة
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-file-signature input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="secondPartyPowerOfAttorneyNumber"
                      value={formData.secondPartyPowerOfAttorneyNumber}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Property Information */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-home"></i> {t('forms.propertyType', 'Property Type')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-home input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="propertyType"
                  value={formData.propertyType}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-map-marker-alt"></i> {t('forms.propertyDistrict', 'Property District')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-map-marker-alt input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="propertyDistrict"
                  value={formData.propertyDistrict}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-hashtag"></i> {t('forms.propertyPlotNumber', 'Plot Number')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-hashtag input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="propertyPlotNumber"
                  value={formData.propertyPlotNumber}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-calendar-check"></i> {t('forms.periodFrom', 'Period From')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-calendar-check input-icon"></i>
                <input
                  type="date"
                  className="form-input"
                  name="periodFrom"
                  value={formData.periodFrom}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-calendar-times"></i> {t('forms.periodTo', 'Period To')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-calendar-times input-icon"></i>
                <input
                  type="date"
                  className="form-input"
                  name="periodTo"
                  value={formData.periodTo}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          {/* Clauses */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-paragraph"></i> {t('forms.clause1', 'Clause 1')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-paragraph input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="clause1"
                  value={formData.clause1}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-paragraph"></i> {t('forms.clause2', 'Clause 2')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-paragraph input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="clause2"
                  value={formData.clause2}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-paragraph"></i> {t('forms.clause3', 'Clause 3')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-paragraph input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="clause3"
                  value={formData.clause3}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-paragraph"></i> {t('forms.clause4', 'Clause 4')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-paragraph input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="clause4"
                  value={formData.clause4}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-paragraph"></i> {t('forms.clause5', 'Clause 5')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-paragraph input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="clause5"
                  value={formData.clause5}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-phone"></i> {t('forms.phone1', 'Phone 1')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-phone input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="phone1"
                  value={formData.phone1}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-phone"></i> {t('forms.phone2', 'Phone 2')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-phone input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="phone2"
                  value={formData.phone2}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-phone"></i> {t('forms.phone3', 'Phone 3')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-phone input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="phone3"
                  value={formData.phone3}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-envelope"></i> {t('forms.email', 'Email')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-envelope input-icon"></i>
                <input
                  type="email"
                  className="form-input"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-map-marker-alt"></i> {t('forms.addressAr', 'Address (Arabic)')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-map-marker-alt input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="addressAr"
                  value={formData.addressAr}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-map-marker-alt"></i> {t('forms.addressEng', 'Address (English)')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-map-marker-alt input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="addressEng"
                  value={formData.addressEng}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          {/* Signatures */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-signature"></i> {t('forms.firstPartySignature', 'First Party Signature')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-signature input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="firstPartySignature"
                  value={formData.firstPartySignature}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-signature"></i> {t('forms.secondPartySignature', 'Second Party Signature')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-signature input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="secondPartySignature"
                  value={formData.secondPartySignature}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handlePrint}
            >
              <i className="fas fa-print"></i> {t('forms.print', 'Print to PDF')}
            </button>
          </div>
        </form>
      </div>

      {/* Print Preview */}
      <div ref={printRef} className="print-container" style={{ display: 'none' }}>
        <div className="reconciliation-print">
          {/* Print Header with Organization Details */}
          <div style={{ marginBottom: '20px' }}>
            <PrintHeader 
              title={t('forms.annualReconciliation', 'Annual Reconciliation')}
              showDate={true}
            />
          </div>
          <div className="reconciliation-title">
            <h2>مخالصة سنوية لإدارة الأملاك</h2>
            <h3>Annual Reconciliation for Property Management</h3>
          </div>
          <div className="parties-info">
            <div className="party-box">
              <h4>الطرف الأول / First Party</h4>
              <p>السيد / السيدة: {formData.firstPartyName || '______________'}</p>
              <p>رقم الهوية / ID No.: {formData.firstPartyId || '______________'}</p>
              <p>الجنسية / Nationality: {formData.firstPartyNationality || '______________'}</p>
            </div>
            <div className="party-box">
              <h4>الطرف الثاني / Second Party</h4>
              {formData.secondPartyType === 'individual' ? (
                <>
                  <p>{formData.secondParty || '______________'}</p>
                  <p>{formData.secondPartyEng || '______________'}</p>
                </>
              ) : (
                <>
                  <p><strong>اسم الشركة / Company Name:</strong> {formData.secondPartyCompanyName || '______________'}</p>
                  <p>{formData.secondPartyCompanyNameEng || '______________'}</p>
                  <p><strong>السجل التجاري / Commercial Registration:</strong> {formData.secondPartyCommercialRegistration || '______________'}</p>
                  <p><strong>اسم الوكيل / Agent Name:</strong> {formData.secondPartyAgentName || '______________'}</p>
                  <p><strong>رقم هوية الوكيل / Agent ID:</strong> {formData.secondPartyAgentId || '______________'}</p>
                  <p><strong>رقم الوكالة / Power of Attorney:</strong> {formData.secondPartyPowerOfAttorneyNumber || '______________'}</p>
                </>
              )}
            </div>
          </div>
          <div className="reconciliation-clauses">
            <div className="clause">
              <p><strong>أولاً / Firstly:</strong></p>
              <p>{formData.clause1 || '______________'}</p>
              <p>النوع / Type: {formData.propertyType || '______________'}</p>
              <p>الواقعة بحي / Located in district: {formData.propertyDistrict || '______________'}</p>
              <p>قطعة رقم / Plot No.: {formData.propertyPlotNumber || '______________'}</p>
              <p>للفتره من... حتي... / Period from {formData.periodFrom || '______________'} until {formData.periodTo || '______________'}</p>
            </div>
            <div className="clause">
              <p><strong>ثانياً / Secondly:</strong></p>
              <p>{formData.clause2 || '______________'}</p>
            </div>
            <div className="clause">
              <p><strong>ثالثاً / Thirdly:</strong></p>
              <p>{formData.clause3 || '______________'}</p>
            </div>
            <div className="clause">
              <p><strong>رابعاً / Fourthly:</strong></p>
              <p>{formData.clause4 || '______________'}</p>
            </div>
            <div className="clause">
              <p><strong>خامساً / Fifthly:</strong></p>
              <p>{formData.clause5 || '______________'}</p>
            </div>
          </div>
          <div className="reconciliation-signatures">
            <div className="signature-box">
              <p>الطرف الأول / First Party</p>
              <p>{formData.firstPartySignature || '______________'}</p>
            </div>
            <div className="signature-box">
              <p>الطرف الثاني / Second Party</p>
              <p>{formData.secondPartySignature || '______________'}</p>
              <p>(Mohammed Al-Salmi Real Estate Services Office)</p>
            </div>
          </div>
          <div className="reconciliation-footer">
            <p>{formData.addressAr} - {formData.addressEng}</p>
            <p>Tel: {formData.phone1} {formData.phone2 && `- ${formData.phone2}`} {formData.phone3 && `- ${formData.phone3}`}</p>
            <p>Email: {formData.email}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnnualReconciliationForm;

